import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useProperty,
} from '@vibesites/runtime/react';
import {
  keepLocationTogether,
  keepPropertyNameTogether,
  stripWrappingParagraph,
} from '../utils/keepLocationTogether';
import {
  INNER_PAGE_HERO_SUBTITLE_CLASS,
  INNER_PAGE_HERO_TITLE_CLASS,
  InnerPageHeroSection,
} from './shared/InnerPageHeroSection';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '1 bedroom floor plans banner',
    },
  },
};

export function OneBedroomHero() {
  const property = useProperty();

  const subtitleRaw = useContentToken('onebedroomhero.subtitle');
  const subtitleResolved = useMemo(() => {
    if (!subtitleRaw) return '';
    let html = resolveRichText(subtitleRaw, property as Record<string, any>);
    html = stripWrappingParagraph(html);
    html = keepPropertyNameTogether(html, property.property_name as string | null | undefined);
    return keepLocationTogether(
      html,
      property.property_address_city as string | null | undefined,
      property.property_address_state as string | null | undefined,
    );
  }, [subtitleRaw, property]);

  return (
    <InnerPageHeroSection
      bgGroupPath="onebedroomhero.bg_image"
      bgImageDef={bgImageDef}
      altFallback="1 bedroom floor plans banner"
    >
      <EditableToken
        path="onebedroomhero.subtitle"
        type="richtext"
        as="h2"
        className={INNER_PAGE_HERO_SUBTITLE_CLASS}
      >
        <span
          data-vibesite-richtext=""
          dangerouslySetInnerHTML={{ __html: subtitleResolved }}
          style={{ display: 'contents' }}
        />
      </EditableToken>
      <RichTextContent
        path="onebedroomhero.title"
        as="h1"
        className={INNER_PAGE_HERO_TITLE_CLASS}
      />
      <EditableSlotGroup
        groupPath="onebedroomhero.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </InnerPageHeroSection>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'OneBedroomHero',
  displayName: '1 Bedroom Hero',
  tokens: {
    'onebedroomhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>1 Bedroom</p>',
    },
    'onebedroomhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>1-Bedroom Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
      description:
        'Short, SEO-style headline for the 1-bedroom floor plans page (e.g. "1-Bedroom Apartments in Tigard, OR").',
    },
  },
  slotGroups: {
    'onebedroomhero.bg_image': bgImageDef,
  },
};

(OneBedroomHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default OneBedroomHero;
